import { ITrainingPlanModel } from "@/hooks/game/training-plans/training-plans-model";
import { ITrainingPlanProgressData } from "@/hooks/game/training-plans/training-plans-shared";
import { Events } from "@Context/common/general/context";
import { GameModes, PlaySessionTypes } from "@Enums/index";
import PhaseOfPlay from "@Enums/PhaseOfPlay";
import PlayPositions from "@Enums/PlayPositions";
import { PlanItemRecord } from "@Shared/schemas/training-plans/trainingPlanSchemas";
import { format } from "date-fns";

export interface ITrainingPlanCareerLaunchConfig {
  source: "training-plan";
  playMode: "career";
  playPosition?: PlayPositions;
}

export interface ITrainingPlanPracticeLaunchConfig {
  source: "training-plan";
  playMode: "practice";
  playPosition?: PlayPositions;
  scenarioCount?: number;
  phase?: PhaseOfPlay;
}

export type TrainingPlanPlayModeLaunchConfig = ITrainingPlanCareerLaunchConfig | ITrainingPlanPracticeLaunchConfig;

export interface ITrainingPlanNavigationState {
  trainingPlanLaunchConfig?: TrainingPlanPlayModeLaunchConfig;
}

export interface ITrainingPlanEntry {
  id: string;
  title: string;
  subtitle: string;
  isCompleted: boolean;
  isCurrentSession: boolean;
  isClickable: boolean;
  onClick?: () => void;
}

interface ITrainingPlansI18nValues {
  [key: string]: string | number;
}

type TrainingPlansI18n = (key: string, values?: ITrainingPlansI18nValues) => string;

const getPlanItemDestination = (item: PlanItemRecord | undefined) => {
  if (!item) {
    return null;
  }

  if (item.game_mode === GameModes.MATCH_PLAY) {
    return "/home/matchplay";
  }

  if (item.session_type === PlaySessionTypes.PROGRESSION_SESSION) {
    return "/home/career";
  }

  if (item.session_type === PlaySessionTypes.CHALLENGE_SESSION) {
    return "/challenges";
  }

  return "/home/practise";
};

const getPlanItemSummary = (item: PlanItemRecord | undefined, i18n: TrainingPlansI18n) => {
  const position = item?.play_position ? i18n(`position.${item.play_position}`) : null;
  const scenarioCount = item?.scenario_count ? i18n("training-plans.drawer.entry.practice.count", { count: item.scenario_count || "" }) : null;
  if (!item) {
    return i18n("training-plans.drawer.entry.recommended-session");
  }

  if (item.game_mode === GameModes.MATCH_PLAY) {
    return i18n("training-plans.drawer.entry.match-play");
  }

  if (item.session_type === PlaySessionTypes.PROGRESSION_SESSION) {
    return i18n("training-plans.drawer.entry.career") + (position ? ` · ${position}` : "");
  }

  if (item.session_type === PlaySessionTypes.CHALLENGE_SESSION) {
    return i18n("training-plans.drawer.entry.challenges");
  }

  return i18n("training-plans.drawer.entry.practice") + (position ? ` · ${position}` : "") + (scenarioCount ? ` · ${scenarioCount}` : "");
};

const getTrainingPlanLaunchConfig = (item: PlanItemRecord | undefined): TrainingPlanPlayModeLaunchConfig | undefined => {
  if (!item) {
    return undefined;
  }

  if (item.session_type === PlaySessionTypes.PROGRESSION_SESSION) {
    return {
      source: "training-plan",
      playMode: "career",
      playPosition: item.play_position ?? undefined,
    };
  }

  if (item.session_type === PlaySessionTypes.PRACTISE_SESSION) {
    return {
      source: "training-plan",
      playMode: "practice",
      playPosition: item.play_position ?? undefined,
      scenarioCount: item.scenario_count ?? undefined,
      phase: item.phase ?? undefined,
    };
  }

  return undefined;
};

export const buildTrainingPlanDrawerEntries = (
  progressData: ITrainingPlanProgressData | null,
  trainingPlanModel: ITrainingPlanModel | null,
  i18n: TrainingPlansI18n
): ITrainingPlanEntry[] => {
  if (!progressData?.current_period || !trainingPlanModel) {
    return [];
  }

  const qualifyingSessions = [...progressData.qualifying_sessions].sort(
    (left, right) => new Date(left.counted_at).getTime() - new Date(right.counted_at).getTime()
  );
  const currentSessionIndex = trainingPlanModel.isComplete ? -1 : trainingPlanModel.completedSessionsCount;

  const entries = Array.from({ length: trainingPlanModel.requiredSessionsCount }, (_, index) => {
    const qualifyingSession = qualifyingSessions[index];
    const suggestedItem = progressData.suggested_items[index];
    const destination = getPlanItemDestination(suggestedItem);
    const launchConfig = getTrainingPlanLaunchConfig(suggestedItem);
    const isCurrentSession = index === currentSessionIndex;
    const isClickable = !qualifyingSession && destination;

    return {
      id: `training-plan-day-${index + 1}`,
      title: i18n("training-plans.drawer.entry.day", { day: index + 1 }),
      subtitle: qualifyingSession
        ? i18n("training-plans.drawer.entry.completed-on", { date: format(new Date(qualifyingSession.counted_at), "MMM d, yyyy") })
        : getPlanItemSummary(suggestedItem, i18n),
      isCompleted: Boolean(qualifyingSession),
      isCurrentSession,
      isClickable,
      onClick: isClickable
        ? () =>
            Events.app.navigate.emit({
              path: destination,
              data: launchConfig ? { trainingPlanLaunchConfig: launchConfig } : undefined,
            })
        : undefined,
    };
  });

  return entries;
}